import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import type {
  BrazilianUF,
  CreatePriceTableFormData,
} from '@/schemas/commercial/price-table.schema';

export function useBillingConditions(gi?: number) {
  const { control } = useFormContext<CreatePriceTableFormData>();

  const {
    fields: groupFields,
    append: appendGroup,
    remove: removeGroup,
  } = useFieldArray({
    control,
    name: 'groups',
    keyName: 'rhfId',
  });

  const uid = () => crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2);

  const createEmptyCondition = () => ({
    id: uid(),
    salesFor: [] as BrazilianUF[],
    billingLimitInCents: '',
    toBillFor: '',
  });

  const {
    fields: condFields,
    append: appendCond,
    remove: removeCond,
  } = useFieldArray({
    control,
    name: gi !== undefined ? `groups.${gi}.conditions` : 'groups.0.conditions',
    keyName: 'rhfId',
  });

  // Watch all groups (to block already used UFs)
  const allGroups = useWatch({
    control,
    name: 'groups',
  }) as Array<{
    id: string;
    priority?: boolean;
    conditions: Array<{
      id: string;
      salesFor?: BrazilianUF[];
      toBillFor?: string;
      billingLimitInCents?: string;
    }>;
  }>;

  // UFs used by OTHER groups
  const usedInOtherGroups = new Set<string>();
  (allGroups ?? []).forEach((g, idx) => {
    if (gi !== undefined && idx === gi) return;
    g?.conditions?.forEach((c) => {
      (c?.salesFor ?? []).forEach((uf) => {
        usedInOtherGroups.add(uf);
      });
    });
  });

  // Helpers for UF Combobox
  // Only blocks UFs already used by other groups.
  // Keeps the ones already selected in the current condition.
  const getUfOptionsFor = (ufList: { id: string; text: string }[], currentValue?: string[]) => {
    const current = new Set(currentValue ?? []);
    return ufList.filter((opt) => !usedInOtherGroups.has(opt.id) || current.has(opt.id));
  };

  const isUfDisabled = (ufId: string, currentValue?: string[]) => {
    const current = new Set(currentValue ?? []);
    return usedInOtherGroups.has(ufId) && !current.has(ufId);
  };

  return {
    groupFields,
    appendGroup,
    removeGroup,
    condFields,
    appendCond,
    removeCond,

    // factories
    createEmptyCondition,
    uid,

    control,
    getUfOptionsFor,
    isUfDisabled,
  };
}
